import * as React from 'react';
import {
    Popover,
    Typography,
    Button,
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    IconButton,
    Theme,
    useTheme,
    TextField,
    InputAdornment,
} from '@mui/material';
import {
    AccordianMinusIcon,
    AccordianPlusIcon,
    InvertedDrakDropDownIcon,
    DialogCloseIcon,
    SearchIcon,
} from '../../../../assets/SvgIcons';
import { FilterCheckbox, FilterLoader } from '../../common/CustomFilter';

export default function CustomPopoverFilter({
    isLoading = false,
    filterData = {},
    handleFilterChange,
    title,
    hasSubFilters = false,
    searchable = false,
    selectedFilters = [],
    getLabel = (item) => item?.label,
    getValue = (item) => item?.value,
    getChecked = (item) => !!item?.checked,
}: {
    isLoading?: boolean;
    filterData?: any;
    handleFilterChange: (...args: any[]) => void;
    title: string;
    hasSubFilters?: boolean;
    searchable?: boolean;
    selectedFilters?: any[];
    getLabel?: (item: any) => string;
    getValue?: (item: any) => string;
    getChecked?: (item: any) => boolean;
}): JSX.Element {
    const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);
    const [expandedKeys, setExpandedKeys] = React.useState<Set<string>>(new Set());
    const [searchText, setSearchText] = React.useState('');

    const theme = useTheme();
    const open = Boolean(anchorEl);
    const id = open ? 'filter-popover' : undefined;

    const toggleExpand = (key: string) => {
        setExpandedKeys((prev) => {
            const newSet = new Set(prev);
            newSet.has(key) ? newSet.delete(key) : newSet.add(key);
            return newSet;
        });
    };

    const handleClearSearch = () => setSearchText('');

    const filteredFlatData = !hasSubFilters
        ? (Array.isArray(filterData) ? filterData : []).filter((obj) =>
            getLabel(obj)?.toLowerCase()?.includes(searchText?.toLowerCase())
        )
        : [];


    const categoryHeadFunds = selectedFilters?.filter((obj: any) => !(obj?.subCategoryId?.toLowerCase().includes('_all')));


    return (
        <Box>
            {/* Trigger Button */}
            <Button
                aria-describedby={id}
                variant="outlined"
                onClick={(e) => setAnchorEl(e.currentTarget)}
                sx={{
                    border: selectedFilters.length > 0 ? `1px solid ${theme.palette.primary[100]}` : `1px solid ${theme.palette.common.white}`,
                    borderRadius: '25px',
                    padding: '5px 10px',
                    backgroundColor: selectedFilters.length > 0 ? theme.palette.primary[50] : theme.palette.common.white,
                    gap: 3,
                    flex: 1,
                    boxShadow: `0px 4px 10px 0px ${theme.palette.common.black}0D`,
                    color: theme.palette.text.primary,
                    ...theme.typography.body_500,
                    '&:hover': {
                        backgroundColor: theme.palette.primary[100],
                    },
                }}
            >
                <Box
                    sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        columnGap: 1,
                        minWidth: 0,
                    }}
                >
                    <Typography variant="body_500" sx={{ whiteSpace: 'nowrap' }}>
                        {title}
                    </Typography>
                    {selectedFilters.length > 0 && (
                        <Typography
                            variant="body_500"
                            sx={{
                                backgroundColor: 'primary.200',
                                borderRadius: '999px',
                                padding: '1px 7px',
                                lineHeight: 1.4,
                            }}
                        >
                            {title === "Category" ? categoryHeadFunds.length : selectedFilters.length}
                        </Typography>
                    )}
                </Box>
                <IconButton
                    sx={{
                        color: theme.palette.text.primary,
                        transform: !open ? 'rotate(0deg)' : 'rotate(180deg)',
                        transition: 'transform 0.3s ease-in-out',
                        p: 0,
                    }}
                >
                    <InvertedDrakDropDownIcon width={14} height={14} />
                </IconButton>
            </Button>

            {/* Popover */}
            <Popover
                id={id}
                open={open}
                anchorEl={anchorEl}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                sx={{
                    '& .MuiPopover-paper': {
                        mt: 1,
                        minWidth: '260px',
                        borderRadius: '20px',
                        minHeight: '200px',
                        maxHeight: '400px',
                        overflow: 'auto',
                        '&::-webkit-scrollbar': { display: 'none' },
                    },
                }}
            >
                <Box sx={{ p: 2 }}>
                    {isLoading ? (
                        <FilterLoader />
                    ) : (
                        <>
                            {/* Search Bar (Optional) */}
                            {searchable && !hasSubFilters && (
                                <TextField
                                    fullWidth
                                    placeholder={`Search ${title}`}
                                    value={searchText}
                                    onChange={(e) => setSearchText(e.target.value)}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon />
                                            </InputAdornment>
                                        ),
                                        endAdornment: searchText && (
                                            <InputAdornment position="end">
                                                <IconButton onClick={handleClearSearch}>
                                                    <DialogCloseIcon />
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                        sx: {
                                            borderRadius: '57px',
                                            height: '47px',
                                            border: `1px solid ${theme.palette.text.secondary}B2`,
                                            '& fieldset': { border: 'none' },
                                            '& input': {
                                                paddingLeft: '10px',
                                                ...theme.typography.body_500,
                                                color: theme.palette.text.primary,
                                                '::placeholder': {
                                                    ...theme.typography.subtitle_400,
                                                    color: theme.palette.text.secondary,
                                                    opacity: 1,
                                                },
                                            },
                                        },
                                    }}
                                />
                            )}

                            {/* Selected filters (Optional Section) */}
                            {searchable && selectedFilters.length > 0 && !hasSubFilters && (
                                <Box
                                    sx={{
                                        maxHeight: '150px',
                                        overflow: 'auto',
                                        mt: 2,
                                        pb: 2,
                                        borderBottom: `0.7px solid ${theme.palette.text.secondary}B2`,
                                    }}
                                >
                                    {selectedFilters.map((obj) => (
                                        <FilterCheckbox
                                            key={getValue(obj)}
                                            value={getValue(obj)}
                                            label={getLabel(obj)}
                                            checked={getChecked(obj)}
                                            handleChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                                handleFilterChange(getValue(obj), e.target.checked)
                                            }
                                        />
                                    ))}
                                </Box>
                            )}

                            {/* Main Filter List */}
                            {hasSubFilters ? (
                                Object.keys(filterData).map((parentKey) => {
                                    const items = filterData[parentKey] || [];
                                    const subCount = items.length ? items.length - 1 : 0;
                                    const isExpanded = expandedKeys.has(parentKey);

                                    return (
                                        <Accordion
                                            key={parentKey}
                                            expanded={isExpanded}
                                            onChange={() => toggleExpand(parentKey)}
                                            sx={{
                                                boxShadow: 'none',
                                                '&:before': { display: 'none' },
                                            }}
                                        >
                                            <AccordionSummary
                                                expandIcon={
                                                    isExpanded ? (
                                                        <AccordianMinusIcon />
                                                    ) : (
                                                        <AccordianPlusIcon />
                                                    )
                                                }
                                                sx={{
                                                    padding: 0,
                                                    '& .MuiAccordionSummary-content': { margin: 0 },
                                                }}
                                            >
                                                <FilterCheckbox
                                                    value={getValue(items[0])}
                                                    label={`${parentKey} (${subCount})`}
                                                    checked={getChecked(items[0])}
                                                    handleChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                                        handleFilterChange(
                                                            parentKey,
                                                            items[0]?.subCategoryId,
                                                            e.target.checked
                                                        )
                                                    }
                                                />
                                            </AccordionSummary>
                                            <AccordionDetails sx={{ padding: '0px 0px 0px 20px' }}>
                                                <Box sx={{ maxHeight: '148px', overflow: 'auto' }}>
                                                    {items
                                                        .filter(
                                                            (obj: any) =>
                                                                obj?.subCategoryId !==
                                                                `${parentKey.toLowerCase()}_all`
                                                        )
                                                        .map((obj: any) => (
                                                            <FilterCheckbox
                                                                key={getValue(obj)}
                                                                value={getValue(obj)}
                                                                label={getLabel(obj)}
                                                                checked={getChecked(obj)}
                                                                handleChange={(
                                                                    e: React.ChangeEvent<HTMLInputElement>
                                                                ) =>
                                                                    handleFilterChange(
                                                                        parentKey,
                                                                        obj?.subCategoryId,
                                                                        e.target.checked
                                                                    )
                                                                }
                                                            />
                                                        ))}
                                                </Box>
                                            </AccordionDetails>
                                        </Accordion>
                                    );
                                })
                            ) : (
                                <Box sx={{ mt: searchable ? 2 : 0, maxHeight: '250px', overflow: 'auto' }}>
                                    {filteredFlatData.map((obj) => (
                                        <FilterCheckbox
                                            key={getValue(obj)}
                                            value={getValue(obj)}
                                            label={getLabel(obj)}
                                            checked={getChecked(obj)}
                                            handleChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                                handleFilterChange(getValue(obj), e.target.checked)
                                            }
                                        />
                                    ))}
                                </Box>
                            )}
                        </>
                    )}
                </Box>
            </Popover>
        </Box>
    );
}
